/* Nahtlose Endlos-Schleife fuer Slider mit CSS Scroll Snap.
 *
 * Dawns <slideshow-component> springt am Ende sichtbar zurueck: von der letzten
 * Slide scrollt es durch alle mittleren hindurch auf die erste. Dieses Element
 * schiebt stattdessen einfach weiter.
 *
 * Prinzip: vor die erste Slide kommt ein Klon der letzten, hinter die letzte ein
 * Klon der ersten. Landet der Scroll auf einem Klon, wird die Scroll-Position
 * lautlos auf das echte Gegenstueck gesetzt - weil der Inhalt identisch aussieht,
 * ist der Sprung unsichtbar.
 *
 * Erwartetes Markup:
 *   <kmb-seamless-slider>
 *     <ul data-seamless-track class="slider slider--everywhere"> ...Slides... </ul>
 *     <button name="previous">  <button name="next">
 *   </kmb-seamless-slider>
 */
class KmbSeamlessSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.track = this.querySelector('[data-seamless-track]');
    if (!this.track) return;

    this.realSlides = Array.from(this.track.children);
    if (this.realSlides.length < 2) return;

    this.initialised = true;
    this.settleTimer = null;
    this.activeIndex = 1;
    // Ziel einer laufenden programmatischen Fahrt. Solange gesetzt, haelt sich
    // die Wisch-Korrektur heraus - sonst wuerde sie den bewusst angesteuerten
    // Klon als "verrutscht" behandeln und die Animation abbrechen.
    this.pendingTarget = null;

    this.addClones();

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));

    // Punkte sind optional - die Collage hat keine, das Lookbook schon.
    this.dots = Array.from(this.querySelectorAll('.slider-counter__link'));
    this.dots.forEach((dot, position) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        this.goTo(position + 1, true);
      });
    });

    /* Nur noch als Sicherheitsnetz fuers freie Wischen: landet der Finger auf
       einem Klon, wird danach lautlos auf das echte Gegenstueck gesetzt.
       'scrollend' ist dafuer das zuverlaessige Signal; wo es fehlt, faellt es
       auf einen Timer zurueck. Die Pfeile brauchen beides nicht. */
    if ('onscrollend' in window) {
      this.track.addEventListener('scrollend', () => this.handleScrollSettled());
    } else {
      this.handleScroll = this.handleScroll.bind(this);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    // Greift der Nutzer selbst ein, gilt keine programmatische Fahrt mehr -
    // sonst bliebe pendingTarget haengen und die Wisch-Korrektur waere tot.
    ['pointerdown', 'touchstart', 'wheel'].forEach((type) => {
      this.track.addEventListener(type, () => { this.pendingTarget = null; }, { passive: true });
    });

    // Beim Start ohne sichtbare Bewegung auf die erste echte Slide.
    // Bewusst synchron: requestAnimationFrame feuert in Hintergrund-Tabs nicht.
    this.goTo(1, false);

    // Nur auf Breitenaenderungen reagieren. Die Hoehe aendert sich auf Mobil
    // staendig, wenn die Adressleiste ein- und ausfaehrt - das darf die
    // Scroll-Position nicht anfassen.
    this.lastWidth = this.track.clientWidth;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.track.clientWidth === this.lastWidth) return;
      this.lastWidth = this.track.clientWidth;
      this.goTo(this.activeIndex, false);
    });
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.settleTimer) clearTimeout(this.settleTimer);
  }

  /** Alle Slides inklusive der beiden Klone, in DOM-Reihenfolge. */
  get slides() {
    return Array.from(this.track.children);
  }

  get lastIndex() {
    return this.slides.length - 1;
  }

  addClones() {
    const leading = this.cloneSlide(this.realSlides[this.realSlides.length - 1]);
    const trailing = this.cloneSlide(this.realSlides[0]);
    this.track.append(trailing);
    this.track.prepend(leading);
  }

  /** Klon ohne IDs, ohne Editor-Attribute und aus Fokus und Screenreader genommen. */
  cloneSlide(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-seamless-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone
      .querySelectorAll('[data-shopify-editor-block]')
      .forEach((element) => element.removeAttribute('data-shopify-editor-block'));
    return clone;
  }

  /** Scroll-Position einer Slide, unabhaengig vom offsetParent. */
  scrollPositionOf(slide) {
    return this.track.scrollLeft + (slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left);
  }

  /** Index der Slide, deren Mitte der Mitte des Tracks am naechsten liegt. */
  nearestIndex() {
    const trackCenter = this.track.scrollLeft + this.track.clientWidth / 2;
    let nearest = 0;
    let smallestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const slideCenter = this.scrollPositionOf(slide) + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - trackCenter);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        nearest = index;
      }
    });

    return nearest;
  }

  goTo(index, smooth) {
    const slide = this.slides[index];
    if (!slide) return;

    const left = this.scrollPositionOf(slide);
    this.activeIndex = index;

    if (smooth) {
      this.pendingTarget = index;
      this.track.scrollTo({ left, behavior: 'smooth' });
    } else {
      this.withoutSmoothScroll(() => {
        this.track.scrollLeft = left;
      });
    }

    this.syncDots();
  }

  /** Markiert den Punkt der aktuell sichtbaren Slide. Klone zeigen auf ihr Original. */
  syncDots() {
    if (!this.dots.length) return;

    let position;
    if (this.activeIndex === 0) {
      position = this.realSlides.length - 1;
    } else if (this.activeIndex === this.lastIndex) {
      position = 0;
    } else {
      position = this.activeIndex - 1;
    }

    this.dots.forEach((dot, index) => {
      const isActive = index === position;
      dot.classList.toggle('slider-counter__link--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /** Setzt die Scroll-Position ohne Animation.
   *  Der dadurch ausgeloeste Settle-Handler ist unschaedlich: das Ziel ist immer
   *  eine echte Slide, dort trifft weder die Klon-Bedingung am Anfang noch die
   *  am Ende zu, der Handler laeuft also genau einmal ins Leere. */
  withoutSmoothScroll(apply) {
    const previousBehavior = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    apply();
    this.track.style.scrollBehavior = previousBehavior;
  }

  /* Am Rand wird zuerst lautlos gesprungen und danach animiert, nicht umgekehrt.
     Der Klon zeigt dasselbe Bild wie die Slide, auf der man gerade steht - der
     Sprung ist deshalb unsichtbar, und die anschliessende Animation laeuft in
     die erwartete Richtung weiter. Vorteil gegenueber "erst scrollen, dann
     korrigieren": es haengt nicht daran, das Ende des Scrollens zu erkennen,
     und der Slider steht danach immer auf einer echten Slide. */
  step(direction) {
    const index = this.nearestIndex();
    const lastRealIndex = this.realSlides.length;

    if (direction > 0 && index >= lastRealIndex) {
      this.goTo(0, false); // Klon der letzten Slide, gleiches Bild
      this.goTo(1, true); // vorwaerts auf die erste echte Slide
      return;
    }

    if (direction < 0 && index <= 1) {
      this.goTo(this.lastIndex, false); // Klon der ersten Slide, gleiches Bild
      this.goTo(lastRealIndex, true); // rueckwaerts auf die letzte echte Slide
      return;
    }

    this.goTo(index + direction, true);
  }

  handleScroll() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.handleScrollSettled(), 120);
  }

  /** Nach dem Ausrollen: auf einem Klon gelandet? Dann lautlos umsetzen. */
  handleScrollSettled() {
    const index = this.nearestIndex();

    // Laeuft gerade eine programmatische Fahrt, ist dies nur ein Zwischenstand.
    if (this.pendingTarget !== null) {
      if (index === this.pendingTarget) this.pendingTarget = null;
      return;
    }

    if (index === 0) {
      this.goTo(this.realSlides.length, false);
    } else if (index === this.lastIndex) {
      this.goTo(1, false);
    } else {
      this.activeIndex = index;
      this.syncDots();
    }
  }
}

if (!customElements.get('kmb-seamless-slider')) {
  customElements.define('kmb-seamless-slider', KmbSeamlessSlider);
}
